// ─────────────────────────────────────────────────────────────────────────────
// NotificationsContext.js — global state for the notification centre (bell).
//
// Owns exactly one SSE connection for the whole app (mounted inside the
// protected route subtree, so it only runs while authenticated). Responsibilities:
//   • load recent history on mount (GET /notifications)
//   • open the live SSE stream and prepend pushed notifications
//   • refetch history on every (re)connect to fill any gap accrued while the
//     stream was down (the backend has no Last-Event-ID replay)
//   • expose markRead / markAllRead with optimistic updates
//
// unreadCount is DERIVED from the loaded list (not a separate counter), so it can
// never drift out of sync with what the panel shows.
// ─────────────────────────────────────────────────────────────────────────────
import {
  createContext, useContext, useState, useEffect,
  useCallback, useMemo, useRef,
} from "react";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  connectNotificationStream,
} from "../services/notificationsService";

// Cap the in-memory list — the bell shows recent activity, not an archive.
const MAX_ITEMS = 100;
const HISTORY_LIMIT = 50;

const NotificationsContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (ctx === null) throw new Error("useNotifications must be used within a NotificationsProvider");
  return ctx;
}

export function NotificationsProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [connected,     setConnected]     = useState(false);
  const [loading,       setLoading]       = useState(true);

  const stopRef = useRef(null);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  // ── Load history (initial mount + on every reconnect to fill gaps) ─────────
  const loadHistory = useCallback(async () => {
    try {
      const data = await getNotifications({ limit: HISTORY_LIMIT });
      setNotifications(data?.notifications ?? []);
    } catch {
      // transient — keep whatever we have; the next reconnect retries.
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Connection lifecycle ───────────────────────────────────────────────────
  useEffect(() => {
    loadHistory();

    stopRef.current = connectNotificationStream({
      onOpen: () => {
        setConnected(true);
        // Fill any gap from while we were disconnected.
        loadHistory();
      },
      onError: () => setConnected(false),
      onNotification: (n) => {
        if (!n?.id) return;
        setNotifications((prev) =>
          prev.some((x) => x.id === n.id)      // dedupe (history refetch can overlap)
            ? prev
            : [n, ...prev].slice(0, MAX_ITEMS),
        );
      },
    });

    return () => { stopRef.current?.(); stopRef.current = null; };
  }, [loadHistory]);

  // ── Mutations (optimistic; reload on failure to resync) ────────────────────
  const markRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await markNotificationRead(id); }
    catch { loadHistory(); }
  }, [loadHistory]);

  const markAllRead = useCallback(async () => {
    if (unreadCount === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await markAllNotificationsRead(); }
    catch { loadHistory(); }
  }, [unreadCount, loadHistory]);

  const value = useMemo(() => ({
    notifications,
    unreadCount,
    connected,
    loading,
    markRead,
    markAllRead,
  }), [notifications, unreadCount, connected, loading, markRead, markAllRead]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
